import React, { useEffect, useMemo, useState } from 'react';
import { auth, db } from '../firebase';
import { collection, query, where, doc, onSnapshot, documentId } from 'firebase/firestore';
import { Link, useNavigate } from 'react-router-dom';
import { Map as MapIcon, AlertTriangle, Layers, Sprout, AlertCircle, Edit3, Plus, FileText, RotateCcw, Clock, CreditCard, BookOpen, Share2, LayoutGrid, ShieldCheck, Settings, ChevronRight, ClipboardCheck, Activity, Zap, Award } from 'lucide-react';
import { fetchFarmLayers, fetchFarmSummary } from '../api';
import { useFarmData } from '../context/FarmDataContext';
import { EcointelLottie, EcoIntelFullBleed, EcointelLottieMark } from '../components/EcoIntelLoader';
import ShareStatusModal from '../components/ShareStatusModal';
import TrajectoryReadout, { TrajIcon } from '../components/TrajectoryReadout';

import { EDX_COLORS, EDX_LABELS, edxBandLabel, edxDecimal, processDecimal, procBand } from '../constants/edxDescriptions';
import { resolveFarmTrajectory } from '../constants/trajectory';
import { useViewComplexity } from '../context/ViewComplexityContext';
import { isReadOnly, ROLES, resolvePlanTier, PLAN_TIERS } from '../constants/roles';
import { useDemoMode } from '../context/DemoModeContext';
import { hasFeature, FEATURES } from '../constants/featureGates';

// The four ecosystem-process bars on the property card, in the four-context
// layer colours (handoffs/01-design-pass.md §3): Water=blue, Energy=brown,
// Mineral=near-black, Community=purple. These are the `layer.*` hues from
// tailwind.config / tokens.css, deliberately distinct from the global
// PROCESS_COLORS used elsewhere — the card uses the context-layer palette.
const CARD_PROCESS_BARS = [
  { key: 'water', label: 'Water', color: 'var(--layer-blue)' },
  { key: 'energy', label: 'Energy', color: 'var(--layer-brown)' },
  { key: 'mineral', label: 'Mineral', color: 'var(--layer-black)' },
  { key: 'community', label: 'Community', color: 'var(--layer-nature)' },
];

// Per-process 1-9 level lookup (AppDevPromptsMngmnt #8). Each process has its
// OWN banding scale, so the authoritative 1-9 level is published per process by
// EcoIntel in the get-farm-summary payload — we must NOT re-band the 0-100
// index in-app (ehiPctToEdxBand is the OVERALL EHI banding and would mis-band a
// single process). We read the published value if present and otherwise show
// no number (never the raw 0-100). Supported shapes, in priority order:
//   currentYear.processEdx[key]  |  currentYear[`${key}_edx`]
function processLevel(currentYear, key) {
  if (!currentYear) return null;
  const candidates = [currentYear?.processEdx?.[key], currentYear?.[`${key}_edx`]];
  for (const v of candidates) {
    const n = Number(v);
    if (Number.isFinite(n) && n >= 1 && n <= 9) return Math.round(n);
  }
  return null;
}

// A process scoring below this (0-100 index) flags the card as "needs attention".
const PROCESS_ATTENTION_THRESHOLD = 50;

/**
 * Derive a single property regime from per-parcel land-use types: one shared
 * type → that type; multiple → "MIXED". Returns null when unknown so the
 * sub-line simply omits it rather than inventing a value.
 */
function deriveRegime(parcels) {
  if (!Array.isArray(parcels)) return null;
  const types = new Set(
    parcels
      .filter((p) => p && p.aggregate !== false)
      .map((p) => (typeof p.type === 'string' ? p.type.trim().toUpperCase() : ''))
      .filter(Boolean)
  );
  if (types.size === 0) return null;
  if (types.size === 1) return [...types][0];
  return 'MIXED';
}

/** Limiting (lowest-scoring) process among the four, from real currentYear values. */
function limitingProcess(currentYear) {
  if (!currentYear) return null;
  let lo = null;
  for (const b of CARD_PROCESS_BARS) {
    const v = currentYear[b.key];
    if (typeof v === 'number' && Number.isFinite(v) && (lo === null || v < lo.val)) {
      lo = { key: b.key, label: b.label, val: v };
    }
  }
  return lo;
}

// --- SUB-COMPONENT: Farm Card ---
const FarmCard = ({ farm, farmIndex, store, complexity, canEdit, demoMode, isPublicDemo = false }) => {
  // For non-admin viewers, the public demo farm is read-only: no edit.
  // Delete moved off the card to the future Setup page (handoff 20 §B).
  const isAdminViewer = demoMode?.isAdmin;
  const showEdit = canEdit && (!isPublicDemo || isAdminViewer);
  const [assetCount, setAssetCount] = useState(null);
  const [summary, setSummary] = useState(null);
  const [curveData, setCurveData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  // "Share your status" badge modal (handoffs/00-foundation.md §D).
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    const getCount = async () => {
      try {
        const assets = await fetchFarmLayers(farm.id);
        setAssetCount(assets.length);
      } catch (e) {
        setAssetCount(0);
      }
    };
    getCount();
  }, [farm.id]);

  useEffect(() => {
    if (farm.status !== 'COMPLETED' && farm.status !== 'READY') return;
    let cancelled = false;
    const loadData = async () => {
      setSummaryLoading(true);
      try {
        const data = await store.fetchWithFirestore(
          'farmSummary', farm.id,
          ['farms', farm.id, 'cache', 'summary'],
          () => fetchFarmSummary(farm.id)
        );
        if (cancelled) return;
        if (!cancelled) setSummary(data);
      } catch (e) {
        // Summary not available - that's OK
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    };
    loadData();
    return () => { cancelled = true; };
  }, [farm.id, farm.status, store]);

  const currentYear = summary?.currentYear;

  // Headline EDX + process scores: get-farm-summary (pixel_scores_field_means via backend); EHI % not on card
  const processScores = {
    energy: currentYear?.energy ?? 50,
    water: currentYear?.water ?? 50,
    mineral: currentYear?.mineral ?? 50,
    community: currentYear?.community ?? 50,
  };

  const edxBand = currentYear?.edx;
  const edxColor = edxBand ? EDX_COLORS[edxBand - 1] : null;
  const edxLabel = currentYear?.edx_label || (edxBand ? EDX_LABELS[edxBand - 1] : null);
  const edxDisplay = edxDecimal(currentYear?.ehi_pct, edxBand) || (edxBand != null ? String(edxBand) : null);
  const trajectoryData = resolveFarmTrajectory(summary);
  const cardDataPending = summaryLoading || assetCount === null;
  const displayFarmName = demoMode.getFarmDisplayName(farm, farmIndex);

  // "Share your status" badge: only offer it on a settled property. The badge
  // is published by ReportBuilder for processed properties, so we hide the
  // affordance while a property is still in-build / paused / errored (the modal
  // also degrades gracefully to a "no badge yet" state if none is published).
  const NOT_READY_FOR_BADGE = new Set(['in_build', 'intake_submitted', 'build_failed', 'build_paused_cancellation']);
  const showShare = !(typeof farm.buildStatus === 'string' && NOT_READY_FOR_BADGE.has(farm.buildStatus))
    && farm.status !== 'ERROR'
    && farm.status !== 'PROCESSING';
  // The badge is published per data year; mirror the analytics default (data
  // typically lags one calendar year) when the summary hasn't loaded a year.
  const badgeYear = currentYear?.year ?? (new Date().getFullYear() - 1);

  // Property card links (Marcus 2026-07-14): drop Analytics; add Assessments /
  // Diagnostics / Actions; Setup is live. Name → Overview.
  const cardLinks = [
    { key: 'overview', label: 'Overview', to: `/overview/${farm.id}`, Icon: LayoutGrid, className: 'text-xs border border-slate-200 rounded px-2 py-2 hover:bg-slate-50 flex items-center justify-center gap-1.5 font-medium text-slate-700' },
    { key: 'map', label: 'Map', to: `/map/${farm.id}`, Icon: MapIcon, className: 'text-xs border border-slate-200 rounded px-2 py-2 hover:bg-slate-50 flex items-center justify-center gap-1.5 font-medium text-slate-700' },
    { key: 'assessments', label: 'Assessments', to: `/assessments/${farm.id}`, Icon: ClipboardCheck, className: 'text-xs border border-slate-200 rounded px-2 py-2 hover:bg-slate-50 flex items-center justify-center gap-1.5 font-medium text-slate-700' },
    { key: 'diagnostics', label: 'Diagnostics', to: `/diagnostics/${farm.id}`, Icon: Activity, className: 'text-xs border border-slate-200 rounded px-2 py-2 hover:bg-slate-50 flex items-center justify-center gap-1.5 font-medium text-slate-700' },
    { key: 'actions', label: 'Actions', to: `/actions/${farm.id}`, Icon: Zap, className: 'text-xs border border-amber-200 rounded px-2 py-2 hover:bg-amber-50 flex items-center justify-center gap-1.5 font-medium text-amber-800' },
    { key: 'disclosure', label: 'Disclosure', to: `/disclosure/${farm.id}`, Icon: ShieldCheck, className: 'text-xs border border-teal-200 rounded px-2 py-2 hover:bg-teal-50 flex items-center justify-center gap-1.5 font-medium text-teal-700' },
    { key: 'reports', label: 'Reports', to: `/reports/${farm.id}`, Icon: BookOpen, className: 'text-xs border border-eddp-primary/30 rounded px-2 py-2 hover:bg-eddp-primary/5 flex items-center justify-center gap-1.5 font-medium text-eddp-primary' },
    { key: 'credentials', label: 'Credentials', to: `/credentials/${farm.id}`, Icon: Award, className: 'text-xs border border-slate-200 rounded px-2 py-2 hover:bg-slate-50 flex items-center justify-center gap-1.5 font-medium text-slate-700' },
    { key: 'setup', label: 'Setup', to: `/setup/${farm.id}`, Icon: Settings, className: 'text-xs border border-slate-200 rounded px-2 py-2 hover:bg-slate-50 flex items-center justify-center gap-1.5 font-medium text-slate-700' },
  ];

  // Sub-line + status (handoffs/01-design-pass.md §3), all from the already
  // loaded summary — no extra network calls on the dashboard.
  const regime = deriveRegime(currentYear?.parcels);
  const totalAreaHa = currentYear?.totalArea;
  const fieldCount = currentYear?.parcelCount;
  const limiting = limitingProcess(currentYear);
  const needsAttention = !!limiting && limiting.val < PROCESS_ATTENTION_THRESHOLD;
  // A settled (not in-build / processing / errored) property with no analysis
  // year yet gets a calm empty state rather than blank bars.
  const settled = !(typeof farm.buildStatus === 'string' && NOT_READY_FOR_BADGE.has(farm.buildStatus))
    && farm.status !== 'ERROR' && farm.status !== 'PROCESSING';
  const showEmptyState = !cardDataPending && settled && !currentYear;

  return (
    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all group relative overflow-hidden" data-tour="dash-farm-card">
      <div className="flex justify-between items-start mb-3">
        {isPublicDemo ? (
          <span className="text-[10px] uppercase font-bold px-2 py-1 rounded-full bg-eddp-teal/15 text-eddp-primary flex items-center gap-1" title="This is a shared demo property available to all users">
            Demo property
          </span>
        ) : <span />}
        {/* Status only — EDX appears in the row below the property name.
            We prefer `farm.buildStatus` (Marcus ingestion v0.3 lifecycle) when
            present, falling back to the legacy `farm.status` enum so the five
            existing properties (asb/bri/dht/pcf/tov) keep their current
            badge unchanged. */}
        {/* Handoff 20 §B: Share sits beside the status badge (top-right). */}
        <div className="flex items-center gap-2">
          {(() => {
            const lifecycle = typeof farm.buildStatus === 'string' ? farm.buildStatus : null;
            if (lifecycle === 'build_failed' || farm.status === 'ERROR') {
              return (
                <span className="text-[10px] uppercase font-bold px-2 py-1 rounded-full bg-red-50 text-red-600 flex items-center gap-1">
                  <AlertTriangle size={10} /> {lifecycle === 'build_failed' ? 'Build failed' : 'Error'}
                </span>
              );
            }
            if (lifecycle === 'build_paused_cancellation') {
              return (
                <span className="text-[10px] uppercase font-bold px-2 py-1 rounded-full bg-amber-50 text-amber-700 flex items-center gap-1" title="Property paused while subscription / entitlement is inactive">
                  <AlertCircle size={10} /> Paused
                </span>
              );
            }
            if (lifecycle === 'in_build') {
              return (
                <span className="text-[10px] uppercase font-bold px-2 py-1 rounded-full bg-amber-50 text-amber-600 flex items-center gap-1">
                  <EcointelLottieMark size={12} /> In build
                </span>
              );
            }
            if (lifecycle === 'intake_submitted') {
              return (
                <span className="text-[10px] uppercase font-bold px-2 py-1 rounded-full bg-amber-50 text-amber-600 flex items-center gap-1">
                  <EcointelLottieMark size={12} /> Intake received
                </span>
              );
            }
            if (lifecycle === 'ready' || farm.status === 'COMPLETED' || farm.status === 'READY') {
              return (
                <span className="text-[10px] uppercase font-bold px-2 py-1 rounded-full bg-green-50 text-green-600">
                  Ready
                </span>
              );
            }
            if (farm.status === 'PROCESSING') {
              return (
                <span className="text-[10px] uppercase font-bold px-2 py-1 rounded-full bg-amber-50 text-amber-600 flex items-center gap-1">
                  <EcointelLottieMark size={12} /> Processing
                </span>
              );
            }
            return (
              <span className="text-[10px] uppercase font-bold px-2 py-1 rounded-full bg-green-50 text-green-600">
                Ready
              </span>
            );
          })()}
          {showShare && (
            <button
              type="button"
              className="shrink-0 text-eddp-primary hover:bg-eddp-primary/5 p-1.5 rounded transition-colors flex items-center"
              onClick={(e) => { e.stopPropagation(); setShareOpen(true); }}
              title="Share your status"
              aria-label="Share your status"
            >
              <Share2 size={16} />
            </button>
          )}
        </div>
      </div>
      
      {/* Name → Overview; pencil → Setup */}
      <div className="flex items-center gap-1.5 mb-1 pr-2">
        <Link
          to={`/overview/${farm.id}`}
          className="font-bold text-slate-800 truncate hover:text-eddp-primary hover:underline min-w-0"
          onClick={(e) => e.stopPropagation()}
          title="Open Overview"
        >
          {displayFarmName}
        </Link>
        {showEdit && (
          <Link
            to={`/setup/${farm.id}`}
            onClick={(e) => e.stopPropagation()}
            className="shrink-0 p-1 text-slate-300 hover:text-eddp-primary hover:bg-slate-100 rounded transition-colors"
            title="Edit property details (Setup)"
            aria-label="Edit property details"
          >
            <Edit3 size={13} />
          </Link>
        )}
      </div>

      {/* EDX summary row */}
      {edxBand && (
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold text-white" style={{ backgroundColor: edxColor }}>EDX {edxDisplay}</span>
          <span className="text-xs text-slate-500">{edxLabel}</span>
          {trajectoryData?.trajectory && trajectoryData.trajectory !== 'INSUFFICIENT_DATA' && (
            // Two-part trajectory (handoff 07): recent direction is the headline
            // chip; a muted long-term sub-line sits directly beneath, keyed on
            // baseline_label. The long-term line is hidden when no baseline is
            // available (never fabricate "steady").
            <span className="inline-flex flex-col leading-tight">
              <TrajectoryReadout trajectory={trajectoryData} compact />
              {trajectoryData.baseline_label && (
                <span className="inline-flex items-center gap-1 text-[10px] text-slate-400 mt-0.5">
                  <TrajIcon dir={trajectoryData.baseline_label} size={9} />
                  Long-term {trajectoryData.baseline_label === 'REGENERATING'
                    ? 'improving'
                    : trajectoryData.baseline_label === 'DEGRADING'
                    ? 'declining'
                    : 'steady'}
                </span>
              )}
            </span>
          )}
        </div>
      )}

      {/* Sub-line: area · fields · regime (handoffs/01-design-pass.md §3). All
          from the loaded summary; parts omitted when unknown. */}
      {currentYear && (totalAreaHa != null || fieldCount != null || regime) && (
        <div className="text-xs text-slate-500 mb-2">
          {[
            totalAreaHa != null ? `${Number(totalAreaHa).toFixed(1)} ha` : null,
            fieldCount != null ? `${fieldCount} ${fieldCount === 1 ? 'field' : 'fields'}` : null,
            regime,
          ].filter(Boolean).join('  ·  ')}
        </div>
      )}

      {/* Handoff 20 §B: generic On track / Needs attention only — no figures
          on the card. Detail lives in Diagnostics for a process alert. */}
      {currentYear && (
        needsAttention ? (
          <div className="flex items-center gap-1.5 text-[11px] text-amber-700 mb-2">
            <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
            <span className="font-medium">Needs attention</span>
            <Link
              to={`/diagnostics/${farm.id}`}
              className="ml-auto text-eddp-primary hover:underline flex items-center gap-0.5 font-medium"
              onClick={(e) => e.stopPropagation()}
            >
              Details in Diagnostics
              <ChevronRight size={11} aria-hidden="true" />
            </Link>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-[11px] text-emerald-700 mb-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
            <span className="font-medium">On track</span>
          </div>
        )
      )}

      {/* Calm empty state for a settled property with no analysis year yet. */}
      {showEmptyState && (
        <div className="text-xs text-slate-400 bg-slate-50 border border-slate-100 rounded-lg px-3 py-3 mb-3">
          No land-health analysis for this property yet. It will appear here once the first assessment is published.
        </div>
      )}

      {/* Asset Count Indicator */}
      <div className="text-xs text-slate-500 mb-4 flex items-center gap-3">
         <span>Uploaded {farm.createdAt?.toDate ? farm.createdAt.toDate().toLocaleDateString() : 'Just now'}</span>
         {assetCount !== null && (
            <span className="flex items-center gap-1 bg-slate-100 px-2 py-0.5 rounded-full text-slate-600">
               <Layers size={10} /> {assetCount} Layers available
            </span>
         )}
      </div>

      {/* Ecosystem-process bars: labelled, in the four-context layer colours,
          with an up-triangle on the limiting process (handoffs/01-design-pass.md
          §3). Per AppDevPromptsMngmnt #8 the readout is the per-process 1-9
          level + band (NOT the raw 0-100 / a %). The bar fill tracks the 1-9
          level when published; until EcoIntel ships the per-process level we
          show the bar without a number rather than the raw 0-100. */}
      {currentYear && complexity?.isVisible?.('dashboard.processBars') && (
        <div className="space-y-1.5 mb-3">
          {CARD_PROCESS_BARS.map((b) => {
            const raw = typeof currentYear[b.key] === 'number' ? currentYear[b.key] : processScores[b.key];
            const procDec = raw != null ? processDecimal(raw, b.key) : null;
            const procBandInt = raw != null ? procBand(raw, b.key) : processLevel(currentYear, b.key);
            const pct = procBandInt != null
              ? (procBandInt / 9) * 100
              : Math.min(Math.max(Number(raw) || 0, 0), 100);
            const isLimiting = limiting && limiting.key === b.key;
            const bandLabel = procBandInt != null ? edxBandLabel(procBandInt) : null;
            return (
              <div key={b.key} className="flex items-center gap-2">
                <div className="w-16 shrink-0 text-[11px] text-slate-500 flex items-center gap-0.5">
                  <span className="truncate">{b.label}</span>
                  {isLimiting && (
                    <span className="text-amber-500 text-[9px] leading-none" title="Limiting process" aria-label="Limiting process">▲</span>
                  )}
                </div>
                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: b.color }} />
                </div>
                {procDec != null ? (
                  <span
                    className="text-right text-[11px] font-mono tabular-nums text-slate-600 shrink-0"
                    title={bandLabel ? `L${procDec} · ${bandLabel}` : `L${procDec}`}
                  >
                    L{procDec}
                  </span>
                ) : (
                  <span className="w-7 shrink-0" aria-hidden="true" />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Carbon headline line (AppDevPromptsMngmnt #8): a single net carbon
          balance in tCO2e/ha/yr from the summary payload, positive = net
          sequestration. Shown only when EcoIntel publishes the value; we never
          invent it. */}
      {(() => {
        const carbonVal = [
          currentYear?.carbon_tco2_ha_yr,
          currentYear?.carbon_tco2_ha,
          summary?.currentYear?.site_nbp_tco2,
        ].find((v) => typeof v === 'number' && Number.isFinite(v));
        if (carbonVal == null) return null;
        return (
          <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-3">
            <Sprout size={12} className="text-eddp-mid shrink-0" />
            <span className="font-mono tabular-nums">
              {carbonVal > 0 ? '+' : ''}{Number(carbonVal).toFixed(1)} tCO₂e/ha/yr
            </span>
          </div>
        );
      })()}

      {/* Card link grid: Overview / Map / Assessments / Diagnostics / Actions /
          Disclosure / Reports / Setup. */}
      <div className="border-t border-slate-50 pt-3 mt-1">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6 }}>
          {cardLinks.map(({ key, label, to, Icon, className }) => (
            <Link
              key={key}
              to={to}
              className={className}
              onClick={(e) => e.stopPropagation()}
            >
              <Icon size={14} className="shrink-0" aria-hidden="true" />
              {label}
            </Link>
          ))}
        </div>
      </div>

      <ShareStatusModal
        open={shareOpen}
        farmId={farm.id}
        year={badgeYear}
        propertyName={displayFarmName}
        onClose={() => setShareOpen(false)}
      />

      {cardDataPending && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center bg-white/90 rounded-xl"
          aria-busy="true"
          aria-live="polite"
        >
          <EcointelLottie
            message="Loading property summary..."
            messages={['Loading property summary...', 'Fetching metrics and assets...']}
            lottiePixelWidth={100}
            lottiePixelHeight={125}
          />
        </div>
      )}
    </div>
  );
};

// --- SUB-COMPONENT: Dummy / "Get started" Property Card --------------------
//
// Shown in place of (or alongside) real property cards when the user has
// no submitted properties yet. Acts as the dashboard-level call to action,
// replacing the previous "Subscribe to add property" top-right button +
// empty-state CTA combo with a single contextual card that mirrors the
// rest of the property grid.
//
// Three lifecycle states, driven by the user's plan tier and whether they
// have an in-flight intake draft:
//
//   1. `subscribe` — Free tier, no subscription yet. CTA: "Subscribe to
//      add your property" → /checkout.
//   2. `submit`    — Paid tier, no intake submitted yet, no draft. CTA:
//      "Submit your property details" → /properties/new + the supporting
//      "We'll set up your property and get data flowing within 3-5 working
//      days" line.
//   3. `resume`    — Paid tier with a saved draft in propertyDrafts/{uid}.
//      Primary CTA: "Resume your property details" → /properties/new.
//      Secondary "Discard draft" affordance lives on the intake form
//      itself (this card stays clean to keep the dashboard tidy).
//
// The card visually echoes the real `FarmCard` shape (same rounded border,
// shadow, padding rhythm) so it feels like part of the grid rather than
// a banner bolted on top.
const DummyPropertyCard = ({ mode, draft, onClick }) => {
  const isSubscribe = mode === 'subscribe';
  const isResume = mode === 'resume';
  const PrimaryIcon = isSubscribe ? CreditCard : isResume ? RotateCcw : FileText;
  const primaryLabel = isSubscribe
    ? 'Subscribe to add your property'
    : isResume
      ? 'Resume your property details'
      : 'Submit your property details';
  const draftSavedAt = (() => {
    if (!isResume || !draft) return null;
    const ts = draft.updatedAt;
    if (!ts) return null;
    if (typeof ts === 'string') {
      const d = new Date(ts);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    if (typeof ts.toDate === 'function') return ts.toDate();
    return null;
  })();
  return (
    <button
      type="button"
      onClick={onClick}
      data-tour="dash-get-started"
      className="bg-white p-5 rounded-xl border-2 border-dashed border-emerald-200 hover:border-eddp-primary hover:bg-emerald-50/30 shadow-sm hover:shadow-md transition-all text-left flex flex-col gap-3 group min-h-[240px]"
    >
      <div className="flex justify-between items-start">
        <span className="text-[10px] uppercase font-bold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 flex items-center gap-1">
          <Sprout size={10} /> Your property
        </span>
        <span className="text-[10px] uppercase font-bold px-2 py-1 rounded-full bg-amber-50 text-amber-700 flex items-center gap-1">
          <Clock size={10} /> {isSubscribe ? 'Get started' : isResume ? 'Draft saved' : 'Not started'}
        </span>
      </div>

      <div>
        <h4 className="font-bold text-slate-800 mb-1">
          {isSubscribe
            ? 'Your property goes here'
            : isResume
              ? 'Your saved property details'
              : 'Let’s build your property'}
        </h4>
        <p className="text-xs text-slate-500 leading-relaxed">
          {isSubscribe
            ? 'Subscribe and we’ll set up your property analytics - all the data you need to understand your land.'
            : isResume
              ? draftSavedAt
                ? `You started filling in your property details ${draftSavedAt.toLocaleDateString()}. Pick up where you left off. We’ll keep your progress safe until you’re ready.`
                : 'You have a saved draft. Pick up where you left off whenever you’re ready.'
              : 'Tell us about your land and we’ll set up your property and get data flowing within 3 to 5 working days.'}
        </p>
      </div>

      <div className="mt-auto pt-3 border-t border-slate-100">
        <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold bg-eddp-primary text-white group-hover:bg-eddp-green2 transition-colors w-full justify-center">
          <PrimaryIcon size={14} /> {primaryLabel}
        </span>
      </div>
    </button>
  );
};

// --- MAIN DASHBOARD ---
export default function Dashboard() {
  const [farms, setFarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState("");
  const [userCanEdit, setUserCanEdit] = useState(false);
  // Phase 2: keep the full user doc around so the "+ Add property" button can
  // consult plan tier + farmIds count to enforce the same cap the server will
  // apply. (Server is the source of truth; this is just UX.)
  const [userData, setUserData] = useState(null);
  // In-flight intake draft, if any. Drives the dummy property card's
  // "Resume your property details" affordance for paid users who started
  // filling in PropertyIntake but didn't submit. `null` = no draft (or
  // listener hasn't fired yet — checked separately via draftLoaded).
  // `intakeDraftError` is set when the listener trips on a Firestore
  // permission error — most commonly because the propertyDrafts/{uid}
  // rule hasn't been redeployed yet. Surfaced as a small inline warning
  // so the user (and any admin) sees something actionable instead of a
  // silently-stale dummy card.
  const [intakeDraft, setIntakeDraft] = useState(null);
  const [intakeDraftLoaded, setIntakeDraftLoaded] = useState(false);
  const [intakeDraftError, setIntakeDraftError] = useState(null);
  const navigate = useNavigate();
  const store = useFarmData();
  const complexity = useViewComplexity();
  const demoMode = useDemoMode();

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    let unsubFarms = null;

    const unsubUser = onSnapshot(doc(db, "users", currentUser.uid), (userSnap) => {
      if (unsubFarms) { unsubFarms(); unsubFarms = null; }

      if (!userSnap.exists()) {
        setUserName(currentUser.displayName || currentUser.email || '');
        setUserData(null);
        setFarms([]);
        setLoading(false);
        return;
      }

      const userData = userSnap.data();
      setUserData({ id: userSnap.id, ...userData });
      const role = userData.role || 'user';
      const assignedIds = userData.farmIds || [];
      setUserName(userData.displayName || userData.businessName || currentUser.displayName || currentUser.email);
      setUserCanEdit(!isReadOnly(role));

      let q;
      if (role === 'admin') {
        q = query(collection(db, "farms"));
      } else if (assignedIds.length === 0) {
        // Phase 1: free / unassigned users still reach the dashboard. They
        // will see the public demo farm(s) merged in below from the
        // DemoModeContext listener. No assigned-farms query needed.
        setFarms([]);
        setLoading(false);
        return;
      } else {
        q = query(collection(db, "farms"), where(documentId(), 'in', assignedIds));
      }

      unsubFarms = onSnapshot(q, (snapshot) => {
        setFarms(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      });
    });

    return () => {
      unsubUser();
      if (unsubFarms) unsubFarms();
    };
  }, []);

  // Live-listen to the user's in-flight intake draft so the dummy
  // property card switches from "Submit your property details" to
  // "Resume your property details" the moment they save progress on
  // /properties/new (or back, when they discard / submit it).
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) return undefined;
    const unsub = onSnapshot(
      doc(db, 'propertyDrafts', currentUser.uid),
      (snap) => {
        setIntakeDraft(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        setIntakeDraftLoaded(true);
        setIntakeDraftError(null);
      },
      (err) => {
        // Non-fatal: rules / network glitch shouldn't break the
        // dashboard. The most common cause is permission-denied because
        // the propertyDrafts/{uid} Firestore rule hasn't been
        // redeployed yet — we capture that so the dummy card can show
        // an inline warning rather than silently flipping back to
        // "Submit your property details" forever.
        console.warn('Dashboard intake-draft listener failed:', err?.message || err);
        setIntakeDraftLoaded(true);
        const code = err?.code || '';
        if (code === 'permission-denied' || /permission/i.test(err?.message || '')) {
          setIntakeDraftError('rules_not_deployed');
        } else {
          setIntakeDraftError('listener_error');
        }
      }
    );
    return () => unsub();
  }, []);

  // Phase 1: merge in public demo farms (DHT and any others flagged
  // `isPublicDemo: true`). Pin them to the top of the list with a badge so the
  // demo property is the user's natural entry point even before they have one
  // of their own.
  const visibleFarms = useMemo(() => {
    const ownIds = new Set(farms.map((f) => f.id));
    const demoExtras = (demoMode.publicDemoFarms || []).filter((f) => !ownIds.has(f.id));
    // Demo farms first; admin-demo-mode scope filter still applies (it returns
    // true for all farms when admin demo mode is off, so this is a no-op for
    // free/paid users and a real filter for admins running a demo session).
    return [...demoExtras, ...farms].filter((f) => demoMode.isFarmInDemoScope(f.id));
  }, [farms, demoMode]);

  // Phase 2: "+ Add property" entitlement. Matches the cap the server will
  // re-check (see backend design proposal):
  //   - admin: unlimited (also bypasses the paywall)
  //   - paid:  1 + (additionalPropertiesAllowed || 0) — admins use the
  //            additionalPropertiesAllowed field as the override to grant
  //            paid users extra slots.
  //   - free:  0 own properties — free users must subscribe before they can
  //            add their first property. They still see the public demo.
  // `farmIds` counts assigned-own properties only — public-demo extras don't
  // count against the cap.
  const ownedFarmCount = Array.isArray(userData?.farmIds) ? userData.farmIds.length : 0;
  const planTier = useMemo(() => resolvePlanTier(userData), [userData]);
  const isAdmin = userData?.role === ROLES.ADMIN;
  const isPaid = planTier === PLAN_TIERS.PAID;
  const propertyCap = useMemo(() => {
    if (isAdmin) return Infinity;
    if (isPaid) return 1 + (Number(userData?.additionalPropertiesAllowed) || 0);
    return 0;
  }, [isAdmin, isPaid, userData]);
  const canAttemptAddProperty = !!userData && hasFeature(userData, FEATURES.ADD_PROPERTY);
  const atOrOverCap = ownedFarmCount >= propertyCap;

  // For free users, the CTA is "Subscribe to add a property" → /checkout.
  // For paid users with capacity, it's "+ Add property" → /properties/new.
  // For paid users at cap, it's disabled with an admin-contact prompt.
  const addPropertyMode = !canAttemptAddProperty
    ? 'hidden'
    : !isAdmin && !isPaid
      ? 'subscribe'
      : atOrOverCap
        ? 'over_cap'
        : 'add';

  // The dummy "get started" property card stands in for the user's
  // first-ever property until they've actually submitted intake. We
  // show it whenever the user has zero own properties AND the
  // ADD_PROPERTY feature is enabled for them — that covers both the
  // pre-subscription state (mode='subscribe') and the post-subscription
  // / pre-intake state (mode='submit' or 'resume' depending on whether
  // a draft exists).
  //
  // We don't show it for admins (their dashboard shows all properties)
  // or while demo mode is active (the demo-mode toggle has its own
  // distinct UI). We also wait for the user doc + draft listener to
  // resolve before rendering, so we don't briefly show "Subscribe" and
  // then flip to "Resume" on the same page-load.
  const dummyCardMode = (() => {
    if (!canAttemptAddProperty) return null;
    if (isAdmin) return null;
    if (demoMode.isDemoModeEnabled) return null;
    if (ownedFarmCount > 0) return null;
    if (!intakeDraftLoaded) return null;
    if (!isPaid) return 'subscribe';
    if (intakeDraft) return 'resume';
    return 'submit';
  })();
  const handleDummyCardClick = () => {
    if (dummyCardMode === 'subscribe') navigate('/checkout');
    else navigate('/properties/new');
  };
  const addPropertyLabel = addPropertyMode === 'subscribe'
    ? 'Subscribe to add property'
    : 'Add property';
  const addPropertyTitle = addPropertyMode === 'subscribe'
    ? 'Subscribe to add your first property. The demo property stays free.'
    : addPropertyMode === 'over_cap'
      ? `You’ve reached your property limit (${propertyCap}). Contact support if you need additional slots.`
      : 'Add a new property to your account';
  const handleAddPropertyClick = () => {
    if (addPropertyMode === 'subscribe') {
      navigate('/checkout');
    } else if (addPropertyMode === 'add') {
      navigate('/properties/new');
    }
  };
  const addPropertyButtonEnabled = addPropertyMode === 'subscribe' || addPropertyMode === 'add';

  // Subscription cancelling banner. Driven entirely by the user doc
  // (mirrored inline by /cancel-subscription + /admin/cancel-user-
  // subscription and reaffirmed by the customer.subscription.updated
  // webhook — see backend/analytics-server/index.js). We show it
  // whenever subscriptionStatus === 'cancelling' so the user has a
  // single, unambiguous "your access ends on X" surface on the
  // dashboard, matching the cancelling state shown on /profile.
  // Admin-override accounts and admins themselves never see this:
  // the override grants access regardless of Stripe state, and admins
  // don't carry Stripe subscriptions at all.
  const isCancelling = userData?.subscriptionStatus === 'cancelling'
    && !isAdmin
    && userData?.subscriptionOverride !== true;
  const cancellingEndDate = (() => {
    if (!isCancelling) return null;
    const raw = userData?.subscriptionCurrentPeriodEnd;
    if (!raw) return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  })();
  const cancellingEndDateLabel = cancellingEndDate
    ? cancellingEndDate.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  return (
    <div className="p-8 max-w-[900px] mx-auto space-y-8">

      {/* Subscription cancelling banner — first thing the user sees
          when their Stripe subscription has been scheduled for
          cancellation. Shows the period-end date pulled from
          users/{uid}.subscriptionCurrentPeriodEnd so the user knows
          exactly when access drops. roles.js treats `cancelling` the
          same as `active` for access purposes, so this banner is the
          ONLY signal on the dashboard that anything has changed. */}
      {isCancelling && (
        <div
          role="status"
          className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 shadow-sm flex items-start gap-3"
        >
          <Clock size={20} className="shrink-0 mt-0.5 text-amber-600" aria-hidden="true" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900">
              Your subscription is scheduled to end
              {cancellingEndDateLabel ? ` on ${cancellingEndDateLabel}` : ' at the end of your current billing period'}.
            </p>
            <p className="text-xs text-amber-800 mt-1">
              You’ll keep full access to your properties and reports until then. After that, your account will revert to the free tier.
              {' '}
              <button
                type="button"
                onClick={() => navigate('/profile')}
                className="underline font-semibold hover:text-amber-900"
              >
                Manage subscription
              </button>
            </p>
          </div>
        </div>
      )}

      {/* Welcome Banner */}
      <div className="bg-eddp-primary rounded-2xl p-8 text-white shadow-xl relative overflow-hidden" data-tour="dash-welcome">
         <div className="absolute top-0 right-0 p-8 opacity-20">
            <img src="/eco-intel-stamp.svg" alt="" className="h-24" />
         </div>
         <div className="relative z-10">
           <h3 className="text-3xl font-bold mb-2">Welcome, {userName.includes('@') ? userName.split('@')[0] : userName}</h3>
           <p className="text-slate-400 max-w-2xl text-lg">
             Select your property below to understand your land.
           </p>
         </div>
      </div>

      {/* Farms Grid */}
      <div>
         <div className="flex items-center justify-between mb-6">
           <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
             <MapIcon className="text-emerald-600" /> Your Properties
           </h3>
           <div className="flex items-center gap-2">
             {/* Count reflects what's actually rendered: real properties
                 (own + demo) plus the dummy "get started" card when it's
                 visible. Keeps the badge in sync with the grid below
                 instead of saying "1 Properties" when the user is looking
                 at a demo card + a dummy card. */}
             <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-xs font-bold">
              {visibleFarms.length + (dummyCardMode ? 1 : 0)} Properties
             </span>
             {/* Phase 2: "+ Add property" CTA.
                 - Hidden for users where the ADD_PROPERTY feature flag is off (e.g. demo role).
                 - For free users → routes to /checkout (subscribe before adding).
                 - For paid users with capacity → routes to /properties/new.
                 - For paid users at cap → disabled with admin-contact tooltip.
                 - When the dummy property card is being shown (zero own
                   properties, ADD_PROPERTY enabled), the top-right button
                   is suppressed because the dummy card IS the CTA — having
                   both would split the user's attention between two
                   identical actions. The button comes back as soon as the
                   user has at least one real property (and so the dummy
                   card disappears). */}
             {addPropertyMode !== 'hidden' && !demoMode.isDemoModeEnabled && !dummyCardMode && (
               <button
                 onClick={handleAddPropertyClick}
                 disabled={!addPropertyButtonEnabled}
                 title={addPropertyTitle}
                 data-tour="dash-add-property"
                 className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${addPropertyButtonEnabled ? 'bg-eddp-primary text-white hover:bg-eddp-green2' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}
               >
                 <Plus size={14} /> {addPropertyLabel}
               </button>
             )}
           </div>
         </div>
         
         {/* Surface a quiet warning when the propertyDrafts listener
             hit a permission error — usually means the Firestore rules
             haven't been redeployed yet, which would otherwise leave
             the dummy card silently stuck on "Submit your property
             details" even after the user has saved a draft. */}
         {intakeDraftError === 'rules_not_deployed' && (
           <div className="mb-4 flex items-start gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-xs">
             <AlertCircle size={14} className="shrink-0 mt-0.5 text-amber-600" />
             <p>
               Couldn’t check for saved property drafts on this account. If you’ve recently saved progress on the intake form and it doesn’t show up here, please contact support. The Firestore rules may need to be redeployed.
             </p>
           </div>
         )}
         {loading ? (
            <EcoIntelFullBleed message="Loading your properties..." minHeight="min-h-[280px]" className="rounded-2xl border border-slate-100" />
         ) : (visibleFarms.length === 0 && !dummyCardMode) ? (
            <div className="text-center py-20 border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50/50">
              <div className="bg-white p-4 rounded-full inline-block shadow-sm mb-4">
                <Sprout className="h-8 w-8 text-slate-300" />
              </div>
              <p className="text-slate-600 font-bold text-lg">
                {demoMode.isDemoModeEnabled ? 'No properties included in demo view yet.' : 'No properties assigned yet.'}
              </p>
              <p className="text-slate-400 mt-1">
                {demoMode.isDemoModeEnabled
                  ? 'Open Admin Settings to include properties for this demo session.'
                  : 'Add your first property below to get started.'}
              </p>
              {!demoMode.isDemoModeEnabled && addPropertyMode !== 'hidden' && (
                <button
                  onClick={handleAddPropertyClick}
                  disabled={!addPropertyButtonEnabled}
                  title={addPropertyTitle}
                  data-tour="dash-add-property"
                  className={`mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${addPropertyButtonEnabled ? 'bg-eddp-primary text-white hover:bg-eddp-green2' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}
                >
                  <Plus size={14} /> {addPropertyMode === 'subscribe' ? 'Subscribe to add a property' : 'Add a property'}
                </button>
              )}
            </div>
         ) : (
            // Auto-fitting grid with a 350px minimum card width so the
            // Map / Analytics / Reports buttons always have room. As the
            // viewport narrows this collapses from 3-up to 2-up to 1-up
            // automatically; the inner min(350px, 100%) clamps the track to
            // the container on very small screens so cards never overflow.
            <div className="grid grid-cols-[repeat(auto-fill,minmax(min(350px,100%),1fr))] gap-6" data-tour="dash-properties">
              {/* Dummy / "Get started" card pinned to the FRONT of the
                  grid for users with no own property yet. Comes BEFORE the
                  demo property so it's the first thing they see — the
                  intent is to make their own onboarding the primary
                  action, with the demo as the supporting "explore" option. */}
              {dummyCardMode && (
                <DummyPropertyCard
                  mode={dummyCardMode}
                  draft={intakeDraft}
                  onClick={handleDummyCardClick}
                />
              )}
              {visibleFarms.map((farm, farmIndex) => (
                <FarmCard 
                  key={farm.id} 
                  farm={farm} 
                  farmIndex={farmIndex}
                  store={store}
                  complexity={complexity}
                  demoMode={demoMode}
                  canEdit={userCanEdit}
                  isPublicDemo={!!farm.isPublicDemo}
                />
              ))}
            </div>
         )}
      </div>
    </div>
  );
}
